import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Avatar,
  Stack,
  Pagination,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Schedule as ScheduleIcon,
  PersonAdd as PersonAddIcon,
  Add as AddIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getDemoCalendarYmdInTimezone, getTodayYMDInTimezone, getViewerTimezone } from '../../utils/dateTimeUtils';
import { demosService } from '../../services/demos';
import { leadsAPI, teachersAPI, salesAPI } from '../../services/api';
import DemoDetails from './DemoDetails';
import DemoEditForm from './DemoEditForm';
import DemoStatusUpdateDialog from './DemoStatusUpdateDialog';
import DemoCreateForm from './DemoCreateForm';
import DemoReadyStudentPicker from '../../components/Demos/DemoReadyStudentPicker';
import { DemoCountryDateLines, DemoCountryFullLines, DemoCountryTimeLines } from '../../components/Demos/DemoCountrySchedule';
import TimeSelector from '../../components/TimeSelector';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { filterLeadsEligibleForDemo } from '../../utils/leadDemoEligibility';
import { invalidateTeacherCalendar } from '../../utils/invalidateTeacherCalendar';
import { getCountries } from '../../data/locationData';
import { formatDemoInstructorLabel } from '../../utils/demoInstructorLabel';

const countryList = getCountries();
const CHANNEL_FILTER_OPTIONS = [
  { value: 'crm_only', label: 'CRM only' },
  { value: 'landing_only', label: 'Landing only' },
  { value: 'both', label: 'CRM + Landing' },
];

const Demos = () => {
  const { isSales, isAdmin, isTeachingLead, user } = useAuth();
  /** Full table, filters, create demo, etc. — admin, sales, teaching leads. Teachers get the simplified card + tabs view only. */
  const showFullDemoManagement = isAdmin() || isSales() || isTeachingLead();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const viewerTimezone = getViewerTimezone(user?.timezone);
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [teachers, setTeachers] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editDemoLoading, setEditDemoLoading] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [openAvailableTeachersDialog, setOpenAvailableTeachersDialog] = useState(false);
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [searchingTeachers, setSearchingTeachers] = useState(false);
  const [timeSlotSearch, setTimeSlotSearch] = useState({
    date: '',
    start_time: '',
    end_time: '',
    duration_minutes: 30
  });
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const dateFrom = params.get('date_from') || '';
    const dateTo = params.get('date_to') || '';
    const teacherId = params.get('teacher_id') || '';
    const country = params.get('country') || '';
    const channel = params.get('channel') || '';
    return {
      status: '',
      type: '',
      teacher_id: teacherId,
      sales_id: '',
      lead_id: '',
      date_from: dateFrom,
      date_to: dateTo,
      include_past: !(dateFrom || dateTo),
      country,
      channel,
    };
  });
  const [openAddLeadDialog, setOpenAddLeadDialog] = useState(false);
  const [selectedDemoForLead, setSelectedDemoForLead] = useState(null);
  const [availableLeads, setAvailableLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState('');
  const [addingLead, setAddingLead] = useState(false);
  const [addLeadSearch, setAddLeadSearch] = useState('');
  const [openAddStudentDialog, setOpenAddStudentDialog] = useState(false);
  const [selectedDemoForStudent, setSelectedDemoForStudent] = useState(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [addingStudents, setAddingStudents] = useState(false);

  const [openCreateDemoDialog, setOpenCreateDemoDialog] = useState(false);
  const [tab, setTab] = useState('all'); // all | today | upcoming
  const [nameSearch, setNameSearch] = useState('');
  const [debouncedNameSearch, setDebouncedNameSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = nameSearch.trim();
      setDebouncedNameSearch((prev) => {
        if (prev !== next) setPage(1);
        return next;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [nameSearch]);

  const fetchDemos = useCallback(async (options = {}) => {
    const silent = options?.silent === true;
    try {
      if (!silent) setLoading(true);
      const params = {
        page,
        limit,
        status: filters.status || undefined,
        type: filters.type || undefined,
        teacher_id: filters.teacher_id || undefined,
        lead_id: filters.lead_id || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        include_past: (filters.include_past || !!debouncedNameSearch) ? 'true' : 'false',
        search: debouncedNameSearch || undefined,
        viewer_timezone: viewerTimezone,
        country: filters.country || undefined,
        channel: filters.channel || undefined,
      };
      if (isAdmin() && filters.sales_id) {
        params.sales_id = filters.sales_id;
      }
      if (!showFullDemoManagement) {
        if (tab === 'today') params.schedule_scope = 'today';
        else if (tab === 'upcoming') params.schedule_scope = 'upcoming';
      }
      const response = await demosService.getDemos(params);
      setDemos(response.data || []);
      setPagination(response.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
    } catch (error) {
      console.error('Error fetching demos:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, page, limit, tab, showFullDemoManagement, isAdmin, viewerTimezone, debouncedNameSearch]);

  useEffect(() => {
    fetchDemos();
  }, [fetchDemos]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await teachersAPI.getAll({
          limit: 500,
          status: 'active',
          include_sales_instructors: true,
        });
        if (!cancelled) {
          setTeachers(res.data?.data || res.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching teachers for filter:', error);
          setTeachers([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchSalesUsersForFilter = useCallback(async () => {
    try {
      const res = await salesAPI.getAll({ page: 1, limit: 500 });
      setSalesUsers(res.data?.data || res.data || []);
    } catch (error) {
      console.error('Error fetching sales users for filter:', error);
      setSalesUsers([]);
    }
  }, []);

  const handleSalesFilterDropdownOpen = useCallback(() => {
    if (salesUsers.length === 0) {
      fetchSalesUsersForFilter();
    }
  }, [fetchSalesUsersForFilter, salesUsers.length]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTabChange = (_event, value) => {
    setTab(value);
    setPage(1);
  };

  // Deep-link filters: /demos?date_from=&date_to=&teacher_id=&country=&channel=
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const dateFrom = params.get('date_from');
    const dateTo = params.get('date_to');
    const teacherId = params.get('teacher_id');
    const country = params.get('country');
    const channel = params.get('channel');
    if (dateFrom == null && dateTo == null && teacherId == null && country == null && channel == null) return;
    setFilters((prev) => {
      const next = {
        ...prev,
        ...(dateFrom != null ? { date_from: dateFrom } : {}),
        ...(dateTo != null ? { date_to: dateTo } : {}),
        ...(teacherId != null ? { teacher_id: teacherId } : {}),
        ...(country != null ? { country } : {}),
        ...(channel != null ? { channel } : {}),
      };
      next.include_past = !(next.date_from || next.date_to);
      return next;
    });
    setPage(1);
    if (dateFrom || dateTo) setTab('all');
  }, [location.search]);

  // Deep-link: /demos?view=<demoId> opens details (e.g. from teacher home)
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const viewId = params.get('view');
    if (!viewId) return;
    if (openDetailsDialog && selectedDemo?.id === viewId) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await demosService.getDemo(viewId);
        if (cancelled) return;
        const demo = res?.data;
        if (!demo) {
          toast.error('Demo not found');
          return;
        }
        setSelectedDemo(demo);
        setOpenDetailsDialog(true);
        params.delete('view');
        navigate(
          { pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' },
          { replace: true }
        );
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          toast.error('Could not open demo');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate, openDetailsDialog, selectedDemo?.id]);

  const handleOpenDetails = async (demo) => {
    setSelectedDemo(demo);
    setOpenDetailsDialog(true);
    if (!demo?.id) return;
    try {
      const response = await demosService.getDemo(demo.id);
      if (response.data) {
        setSelectedDemo(response.data);
      }
    } catch (error) {
      console.error('Error loading demo details:', error);
    }
  };

  const handleCloseDetails = () => {
    setOpenDetailsDialog(false);
    setSelectedDemo(null);
  };

  const handleOpenEdit = async (demo) => {
    setSelectedDemo(demo);
    setOpenEditDialog(true);
    setEditDemoLoading(true);
    if (!demo?.id) {
      setEditDemoLoading(false);
      return;
    }
    try {
      const response = await demosService.getDemo(demo.id);
      if (response.data) {
        setSelectedDemo(response.data);
      }
    } catch (error) {
      console.error('Error loading demo for edit:', error);
    } finally {
      setEditDemoLoading(false);
    }
  };

  const handleCloseEdit = () => {
    setOpenEditDialog(false);
    setEditDemoLoading(false);
    setSelectedDemo(null);
  };

  const handleSearchAvailableTeachers = async () => {
    if (!timeSlotSearch.date || !timeSlotSearch.start_time || !timeSlotSearch.end_time) {
      toast.error('Please fill in all time slot fields');
      return;
    }

    try {
      setSearchingTeachers(true);
      const response = await demosService.searchAvailableTeachers(timeSlotSearch);
      setAvailableTeachers(response.data || []);
      setOpenAvailableTeachersDialog(true);
    } catch (error) {
      console.error('Error searching available teachers:', error);
      toast.error('Failed to search available teachers');
    } finally {
      setSearchingTeachers(false);
    }
  };

  const handleCloseAvailableTeachers = () => {
    setOpenAvailableTeachersDialog(false);
    setAvailableTeachers([]);
  };

  const handleOpenStatusUpdate = async (demo) => {
    try {
      const res = await demosService.getDemo(demo.id);
      setSelectedDemo(res?.data || demo);
    } catch {
      setSelectedDemo(demo);
    }
    setOpenStatusDialog(true);
  };

  const handleCloseStatusUpdate = () => {
    setOpenStatusDialog(false);
    setSelectedDemo(null);
  };

  const handleStatusUpdate = async () => {
    try {
      // StatusUpdateDialog has already handled the API call successfully
      // We just need to refresh the demos list and show success message
      toast.success('Demo status updated successfully');
      handleCloseStatusUpdate();
      fetchDemos();
    } catch (error) {
      console.error('Error updating demo status:', error);
      toast.error('Failed to update demo status');
    }
  };

  const handleDeleteDemo = async (demo) => {
    if (!window.confirm('Are you sure you want to cancel this demo?')) return;
    try {
      const landing = demo.channel === 'landing_only' || demo.channel === 'both';
      if (landing) {
        await demosService.updateStatus(demo.id, { status: 'cancelled' });
      } else {
        await demosService.deleteDemo(demo.id);
      }
      toast.success('Demo cancelled successfully');
      fetchDemos();
    } catch (error) {
      toast.error('Failed to cancel demo');
      console.error('Error cancelling demo:', error);
    }
  };

  const handleDemoEdited = () => {
    fetchDemos();
    invalidateTeacherCalendar(queryClient);
  };

  const handleOpenAddLead = async (demo) => {
    setSelectedDemoForLead(demo);
    setOpenAddLeadDialog(true);
    setSelectedLeadId('');
    setAddLeadSearch('');

    // Fetch available leads: same eligibility as Create Demo; exclude leads already in this demo
    try {
      setLoadingLeads(true);
      const response = await leadsAPI.getAll({ limit: 500 });
      const allLeads = response.data?.leads || response.data || [];
      const leadList = Array.isArray(allLeads) ? allLeads : [];
      const existingLeadIds = (demo.leads?.map(l => String(l.id)) || []).concat(demo.lead_id ? [String(demo.lead_id)] : []);
      const available = filterLeadsEligibleForDemo(leadList).filter(
        (lead) => !existingLeadIds.includes(String(lead.id))
      );
      setAvailableLeads(available);
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to fetch available leads');
      setAvailableLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  };

  const filteredAddLeadLeads = useMemo(() => {
    if (!addLeadSearch?.trim()) return availableLeads;
    const q = addLeadSearch.trim().toLowerCase();
    return availableLeads.filter((l) => {
      const name = (l.name || '').toLowerCase();
      const fullName = [l.first_name, l.last_name].filter(Boolean).join(' ').toLowerCase();
      const phone = (l.phone || '').replace(/\D/g, '');
      const phoneQ = addLeadSearch.replace(/\D/g, '');
      const mother = (l.mother_name || '').toLowerCase();
      const father = (l.father_name || '').toLowerCase();
      const displayId = (l.display_id || '').toLowerCase();
      return (
        name.includes(q) ||
        fullName.includes(q) ||
        displayId.includes(q) ||
        (phoneQ && phone.includes(phoneQ)) ||
        mother.includes(q) ||
        father.includes(q)
      );
    });
  }, [availableLeads, addLeadSearch]);

  const handleCloseAddLead = () => {
    setOpenAddLeadDialog(false);
    setSelectedDemoForLead(null);
    setAvailableLeads([]);
    setSelectedLeadId('');
    setAddLeadSearch('');
  };

  const handleAddLead = async () => {
    if (!selectedLeadId) {
      toast.error('Please select a lead');
      return;
    }

    try {
      setAddingLead(true);
      await demosService.addLeadToDemo(selectedDemoForLead.id, selectedLeadId);
      toast.success('Lead added to demo successfully');
      handleCloseAddLead();
      fetchDemos();
    } catch (error) {
      console.error('Error adding lead to demo:', error);
      toast.error(error.response?.data?.message || 'Failed to add lead to demo');
    } finally {
      setAddingLead(false);
    }
  };

  const handleOpenAddStudent = (demo) => {
    setSelectedDemoForStudent(demo);
    setSelectedStudentIds([]);
    setOpenAddStudentDialog(true);
  };

  const handleCloseAddStudent = () => {
    setOpenAddStudentDialog(false);
    setSelectedDemoForStudent(null);
    setSelectedStudentIds([]);
  };

  const handleAddStudents = async () => {
    if (!selectedStudentIds.length) {
      toast.error('Select at least one student');
      return;
    }
    try {
      setAddingStudents(true);
      await demosService.addStudentsToDemo(selectedDemoForStudent.id, selectedStudentIds);
      toast.success('Student(s) added to demo');
      handleCloseAddStudent();
      fetchDemos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add students');
    } finally {
      setAddingStudents(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled':
        return 'primary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'rescheduled':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'scheduled':
        return 'Scheduled';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'rescheduled':
        return 'Rescheduled';
      default:
        return status;
    }
  };

  const todayYMD = getTodayYMDInTimezone(viewerTimezone);
  // Keep Demo Details (and the search box) mounted during list refreshes
  if (loading && !openDetailsDialog && demos.length === 0 && !nameSearch) {
    return (
      <Container maxWidth="xl">
        <Typography variant="h4" gutterBottom>
          Loading demos...
        </Typography>
      </Container>
    );
  }

  const handleCreateDemoSuccess = () => {
    setOpenCreateDemoDialog(false);
    fetchDemos();
    invalidateTeacherCalendar(queryClient);
    toast.success('Demo created successfully');
  };

  return (
    <Container maxWidth="xl">
      {showFullDemoManagement ? (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4" component="h1">
              Demo Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateDemoDialog(true)}
            >
              Create Demo
            </Button>
          </Box>

          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  fullWidth
                  label="Search demo name"
                  placeholder="Partial name (includes past)"
                  value={nameSearch}
                  onChange={(e) => setNameSearch(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="scheduled">Scheduled</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                    <MenuItem value="rescheduled">Rescheduled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="">All Types</MenuItem>
                    <MenuItem value="demo">Demo</MenuItem>
                    <MenuItem value="trial">Trial</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={filters.country || ''}
                    onChange={(e) => handleFilterChange('country', e.target.value)}
                    label="Country"
                  >
                    <MenuItem value="">All Countries</MenuItem>
                    {countryList.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth>
                  <InputLabel>Channel</InputLabel>
                  <Select
                    value={filters.channel || ''}
                    onChange={(e) => handleFilterChange('channel', e.target.value)}
                    label="Channel"
                  >
                    <MenuItem value="">All Channels</MenuItem>
                    {CHANNEL_FILTER_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth>
                  <InputLabel>Teacher</InputLabel>
                  <Select
                    value={filters.teacher_id}
                    onChange={(e) => handleFilterChange('teacher_id', e.target.value)}
                    label="Teacher"
                  >
                    <MenuItem value="">All Teachers</MenuItem>
                    {teachers.map((teacher) => {
                      const teacherName = formatDemoInstructorLabel(teacher) ||
                        teacher.name || teacher.full_name ||
                        teacher.email || teacher.id;
                      return (
                        <MenuItem key={teacher.id} value={teacher.id}>
                          {teacherName}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              {isAdmin() && (
                <Grid item xs={12} sm={2}>
                  <FormControl fullWidth>
                    <InputLabel>Sales</InputLabel>
                    <Select
                      value={filters.sales_id}
                      onChange={(e) => handleFilterChange('sales_id', e.target.value)}
                      onOpen={handleSalesFilterDropdownOpen}
                      label="Sales"
                    >
                      <MenuItem value="">All Sales</MenuItem>
                      {salesUsers.map((salesUser) => {
                        const salesName = [salesUser.first_name, salesUser.last_name].filter(Boolean).join(' ').trim() ||
                          salesUser.email || salesUser.user_id || salesUser.id;
                        return (
                          <MenuItem key={salesUser.user_id || salesUser.id} value={salesUser.user_id || salesUser.id}>
                            {salesName}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Date From"
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Date To"
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!filters.include_past}
                      onChange={(e) => handleFilterChange('include_past', e.target.checked)}
                    />
                  }
                  label="Include past demos"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setNameSearch('');
                    setDebouncedNameSearch('');
                    setFilters({
                      status: '',
                      type: '',
                      teacher_id: '',
                      sales_id: '',
                      lead_id: '',
                      date_from: '',
                      date_to: '',
                      include_past: true,
                      country: '',
                      channel: '',
                    });
                    setPage(1);
                  }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Find Available Teachers Section */}
          <Paper sx={{ mt: 2, p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchIcon />
              Find Available Teachers
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={timeSlotSearch.date}
                  onChange={(e) => setTimeSlotSearch(prev => ({ ...prev, date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TimeSelector
                  label="Start Time"
                  value={timeSlotSearch.start_time}
                  onChange={(value) => setTimeSlotSearch(prev => ({ ...prev, start_time: value }))}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TimeSelector
                  label="End Time"
                  value={timeSlotSearch.end_time}
                  onChange={(value) => setTimeSlotSearch(prev => ({ ...prev, end_time: value }))}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Duration (minutes)"
                  type="number"
                  value={timeSlotSearch.duration_minutes}
                  onChange={(e) => setTimeSlotSearch(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 30 }))}
                  inputProps={{ min: 15, max: 480, step: 15 }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearchAvailableTeachers}
                  disabled={searchingTeachers}
                  fullWidth
                >
                  {searchingTeachers ? 'Searching...' : 'Search'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {!filters.include_past && !filters.date_from && !filters.date_to && !debouncedNameSearch && (
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              Showing demos from today onward. Check &quot;Include past demos&quot; to see older records.
            </Alert>
          )}

          {pagination.total > 0 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} demos
              </Typography>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                size="medium"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Demos Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Demo Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Lead(s)</TableCell>
                    <TableCell>Teacher</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {demos.map((demo) => (
                    <TableRow key={demo.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {demo.name || 'Untitled Demo'}
                    </Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip
                        size="small"
                        label={
                          demo.channel === 'landing_only'
                            ? 'Landing'
                            : demo.channel === 'both'
                              ? 'CRM + Landing'
                              : 'CRM'
                        }
                        color={demo.channel === 'crm_only' || !demo.channel ? 'default' : 'primary'}
                        variant="outlined"
                      />
                      {(demo.countries || []).slice(0, 3).map((c) => (
                        <Chip key={c} size="small" label={c} variant="outlined" />
                      ))}
                      {(demo.countries || []).length > 3 && (
                        <Chip size="small" label={`+${demo.countries.length - 3}`} variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={demo.type === 'trial' ? 'Trial' : 'Demo'}
                      color={demo.type === 'trial' ? 'primary' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {demo.leads && Array.isArray(demo.leads) && demo.leads.length > 0 ? (
                      <Box>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {demo.leads[0]?.first_name} {demo.leads[0]?.last_name}
                          {demo.leads.length > 1 && ` +${demo.leads.length - 1} more`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {demo.lead_count || demo.leads.length} lead(s)
                          {(demo.student_count > 0 || demo.students?.length > 0) &&
                            ` · ${demo.student_count || demo.students?.length || 0} performer(s)`}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {demo.lead_first_name ? `${demo.lead_first_name} ${demo.lead_last_name}` : 'No leads'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {demo.teacher_first_name ? 
                      formatDemoInstructorLabel({
                        first_name: demo.teacher_first_name,
                        last_name: demo.teacher_last_name,
                        role: demo.teacher_role,
                      }) : 
                      'Not Assigned'
                    }
                  </TableCell>
                  <TableCell sx={{ minWidth: 220 }}>
                    <DemoCountryDateLines demo={demo} viewerTimezone={viewerTimezone} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 260 }}>
                    <DemoCountryTimeLines demo={demo} viewerTimezone={viewerTimezone} />
                  </TableCell>
                  <TableCell>{demo.duration_minutes} min</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(demo.status)}
                      color={getStatusColor(demo.status)}
                      size="small"
                      onClick={() => handleOpenStatusUpdate(demo)}
                      style={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDetails(demo)}
                      title="View Details"
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenAddLead(demo)}
                      title="Add Lead"
                      color="primary"
                    >
                      <PersonAddIcon />
                    </IconButton>
                    {showFullDemoManagement && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenAddStudent(demo)}
                        title="Add student performer"
                        color="secondary"
                      >
                        <SchoolIcon />
                      </IconButton>
                    )}
                    {(demo.status === 'scheduled' || demo.status === 'confirmed' || demo.status === 'rescheduled') && (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEdit(demo)}
                          title="Edit demo"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteDemo(demo)}
                          title="Cancel Demo"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {pagination.total > 0 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} demos
              </Typography>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                size="medium"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      ) : (
        <>
          <Typography variant="h4" fontWeight={900} sx={{ mb: 0.5 }}>
            Demo & Trial Sessions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Manage your upcoming student evaluations
          </Typography>

          <Tabs
            value={tab}
            onChange={handleTabChange}
            sx={{ mb: 2 }}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab value="all" label="All Sessions" sx={{ textTransform: 'none', fontWeight: 800 }} />
            <Tab value="today" label="Today" sx={{ textTransform: 'none', fontWeight: 800 }} />
            <Tab value="upcoming" label="Upcoming" sx={{ textTransform: 'none', fontWeight: 800 }} />
          </Tabs>

          <Stack spacing={1.25}>
            {demos.map((demo) => {
              const name = demo.name || 'Untitled Demo';
              const initials = name
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((p) => p[0]?.toUpperCase())
                .join('');
              const viewerYmd = getDemoCalendarYmdInTimezone(demo, viewerTimezone);
              const isToday = viewerYmd === todayYMD;
              const isUpcoming = viewerYmd && viewerYmd > todayYMD;
              const chipLabel = isToday ? 'TODAY' : (isUpcoming ? 'UPCOMING' : String(demo.status || '').toUpperCase());

              return (
                <Paper
                  key={demo.id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    borderColor: 'rgba(0,0,0,0.08)',
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 260 }}>
                      <Avatar sx={{ bgcolor: '#0b3954', width: 40, height: 40, fontWeight: 900 }}>
                        {initials || 'D'}
                      </Avatar>
                      <Box>
                        <Typography fontWeight={900}>{name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {demo.type === 'trial' ? 'Trial' : 'Demo'}
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{ minWidth: 260 }}>
                      <DemoCountryFullLines demo={demo} viewerTimezone={viewerTimezone} />
                      <Typography variant="caption" color="text.secondary">
                        {demo.duration_minutes || 0} Mins Duration
                      </Typography>
                    </Box>

                    <Chip
                      label={chipLabel}
                      size="small"
                      sx={{
                        fontWeight: 900,
                        bgcolor: isToday ? 'rgba(255,152,0,0.10)' : 'rgba(0,0,0,0.05)',
                        color: isToday ? '#b26a00' : 'text.secondary',
                      }}
                    />

                    <Box sx={{ minWidth: 140, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant={isToday ? 'contained' : 'text'}
                        onClick={() => handleOpenDetails(demo)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 900,
                          borderRadius: 2,
                          px: 2.5,
                          bgcolor: isToday ? '#0b3954' : undefined,
                          color: isToday ? '#fff' : '#0b3954',
                          '&:hover': { bgcolor: isToday ? '#082a3f' : 'rgba(11,57,84,0.08)' },
                        }}
                      >
                        {isToday ? 'Start Demo' : 'View Details'}
                      </Button>
                    </Box>
                  </Stack>
                </Paper>
              );
            })}

            {demos.length === 0 ? (
              <Alert severity="info">No demo/trial sessions found.</Alert>
            ) : null}
          </Stack>

          {pagination.total > 0 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} sessions
              </Typography>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                size="medium"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      {/* Dialogs */}
      <Dialog open={openDetailsDialog} onClose={handleCloseDetails} maxWidth="lg" fullWidth>
        <DialogTitle>Demo Details</DialogTitle>
        <DialogContent>
          {selectedDemo && <DemoDetails demo={selectedDemo} onRefresh={async (demoId) => {
            // Refresh the demos list without unmounting this dialog
            await fetchDemos({ silent: true });
            // Refresh the selected demo details
            if (demoId) {
              try {
                const response = await demosService.getDemo(demoId);
                if (response.data) {
                  setSelectedDemo(response.data);
                }
              } catch (error) {
                console.error('Error refreshing demo details:', error);
              }
            }
          }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditDialog} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle>Edit demo</DialogTitle>
        <DialogContent sx={{ pt: 1.5, pb: 0 }} dividers>
          {editDemoLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedDemo ? (
            <DemoEditForm
              key={selectedDemo.id}
              demo={selectedDemo}
              onSuccess={handleDemoEdited}
              onCancel={handleCloseEdit}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Demo Status Update Dialog */}
      {openStatusDialog && selectedDemo && (
        <DemoStatusUpdateDialog
          demo={selectedDemo}
          onUpdate={handleStatusUpdate}
          onCancel={handleCloseStatusUpdate}
        />
      )}

      {/* Available Teachers Dialog */}
      <Dialog open={openAvailableTeachersDialog} onClose={handleCloseAvailableTeachers} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ScheduleIcon />
            Available Teachers
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              Date: {timeSlotSearch.date} | Time: {timeSlotSearch.start_time} - {timeSlotSearch.end_time} | Duration: {timeSlotSearch.duration_minutes} minutes
            </Typography>
          </Box>
          
          {availableTeachers.length === 0 ? (
            <Alert severity="info">
              No teachers available for the selected time slot.
            </Alert>
          ) : (
            <List>
              {availableTeachers.map((teacher, index) => (
                <React.Fragment key={teacher.id}>
                  <ListItem>
                    <ListItemText
                      primary={formatDemoInstructorLabel(teacher)}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Email: {teacher.email}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Phone: {teacher.phone || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Location: {teacher.city && teacher.state ? `${teacher.city}, ${teacher.state}` : teacher.country || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Demo Ready: {teacher.demo_ready || 'No'}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < availableTeachers.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAvailableTeachers}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create New Demo Dialog */}
      <DemoCreateForm
        open={openCreateDemoDialog}
        onSuccess={handleCreateDemoSuccess}
        onCancel={() => setOpenCreateDemoDialog(false)}
      />

      {/* Add Lead to Demo Dialog */}
      <Dialog open={openAddLeadDialog} onClose={handleCloseAddLead} maxWidth="sm" fullWidth>
        <DialogTitle>Add Lead to Demo</DialogTitle>
        <DialogContent>
          {selectedDemoForLead && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Demo: <strong>{selectedDemoForLead.name || 'Untitled Demo'}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Current leads: {selectedDemoForLead.lead_count || (selectedDemoForLead.leads?.length || 0)}
              </Typography>

              {loadingLeads ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : availableLeads.length === 0 ? (
                <Alert severity="info">
                  No available leads to add. All leads may already be assigned to this demo or registered.
                </Alert>
              ) : (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search leads by name, phone, or parent..."
                    value={addLeadSearch}
                    onChange={(e) => setAddLeadSearch(e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <FormControl fullWidth>
                    <InputLabel id="add-lead-select-label">Select Lead</InputLabel>
                    <Select
                      labelId="add-lead-select-label"
                      id="add-lead-select"
                      value={selectedLeadId || ''}
                      onChange={(e) => setSelectedLeadId(String(e.target.value))}
                      label="Select Lead"
                      MenuProps={{
                        PaperProps: { style: { maxHeight: 300 } },
                        disableScrollLock: true
                      }}
                      renderValue={(val) => {
                        const lead = availableLeads.find((l) => String(l.id) === String(val));
                        if (!lead) return val || '';
                        const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.name || '';
                        const parent = lead.mother_name || lead.father_name || '';
                        const parts = [name, lead.phone, parent].filter(Boolean);
                        return parts.join(' | ');
                      }}
                    >
                      {filteredAddLeadLeads.map((lead) => {
                        const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.name || '';
                        const parent = lead.mother_name || lead.father_name || '';
                        const label = [name, lead.phone, parent ? `Parent: ${parent}` : ''].filter(Boolean).join(' | ');
                        return (
                          <MenuItem key={lead.id} value={String(lead.id)}>
                            {label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Shows New, WIP before demo, Deferred, or Demo scheduled without an active demo.
                    </Typography>
                  </FormControl>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddLead} disabled={addingLead}>
            Cancel
          </Button>
          <Button
            onClick={handleAddLead}
            variant="contained"
            disabled={!selectedLeadId || addingLead || loadingLeads || availableLeads.length === 0}
            startIcon={addingLead ? <CircularProgress size={20} /> : null}
          >
            {addingLead ? 'Adding...' : 'Add Lead'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAddStudentDialog} onClose={handleCloseAddStudent} maxWidth="md" fullWidth>
        <DialogTitle>Add student performer(s)</DialogTitle>
        <DialogContent>
          {selectedDemoForStudent && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Demo: <strong>{selectedDemoForStudent.name || 'Untitled Demo'}</strong>
              </Typography>
              <DemoReadyStudentPicker
                selectedIds={selectedStudentIds}
                onChange={setSelectedStudentIds}
                excludeDemoId={selectedDemoForStudent.id}
                disabled={addingStudents}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddStudent} disabled={addingStudents}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddStudents}
            disabled={!selectedStudentIds.length || addingStudents}
            startIcon={addingStudents ? <CircularProgress size={20} /> : null}
          >
            {addingStudents ? 'Adding…' : 'Add selected'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Demos;
